(() => {
  if (window.__osmodiV4Growth) return;
  window.__osmodiV4Growth = true;

  const q = (selector, root = document) => root?.querySelector(selector) || null;
  const qa = (selector, root = document) => Array.from(root?.querySelectorAll(selector) || []);
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  let toastTimer = 0;
  let menuReturnFocus = null;

  const track = (name, detail = {}) => {
    const payload = { event: `osmodi_${name}`, ...detail };
    if (Array.isArray(window.dataLayer)) window.dataLayer.push(payload);
    document.dispatchEvent(new CustomEvent(`osmodi:${name}`, { detail }));
  };

  const closeNavs = (except = null) => {
    qa('[data-v4-nav-item]').forEach((item) => {
      if (item === except) return;
      q('[data-v4-nav-trigger]', item)?.setAttribute('aria-expanded', 'false');
      const panel = q('[data-v4-nav-panel]', item);
      if (panel) panel.hidden = true;
    });
  };

  const setMenu = (open) => {
    const menu = q('[data-v4-menu]');
    const trigger = q('[data-v4-menu-open]');
    if (!menu) return;
    const wasOpen = !menu.hidden;
    if (open && !wasOpen) menuReturnFocus = document.activeElement instanceof HTMLElement ? document.activeElement : trigger;
    menu.hidden = !open;
    menu.setAttribute('aria-hidden', String(!open));
    trigger?.setAttribute('aria-expanded', String(open));
    document.body.classList.toggle('v4-locked', open);
    if (open) {
      requestAnimationFrame(() => q('a, button, input', q('.v4-menu__panel', menu))?.focus({ preventScroll: true }));
      track('menu_open');
    } else if (wasOpen) {
      requestAnimationFrame(() => {
        const returnTarget = menuReturnFocus?.isConnected ? menuReturnFocus : trigger;
        returnTarget?.focus({ preventScroll: true });
        menuReturnFocus = null;
      });
    }
  };

  const setSearch = (open) => {
    const search = q('[data-v4-search]');
    const trigger = q('[data-v4-search-toggle]');
    if (!search) return;
    search.hidden = !open;
    trigger?.setAttribute('aria-expanded', String(open));
    if (open) {
      requestAnimationFrame(() => q('[data-v4-search-input]', search)?.focus({ preventScroll: true }));
      track('search_open');
    }
  };

  const showToast = (message) => {
    let toast = q('.v4-toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'v4-toast';
      toast.setAttribute('role', 'status');
      toast.setAttribute('aria-live', 'polite');
      document.body.append(toast);
    }
    toast.textContent = message;
    toast.dataset.visible = 'true';
    window.clearTimeout(toastTimer);
    toastTimer = window.setTimeout(() => { toast.dataset.visible = 'false'; }, 2800);
  };

  const updateCartCount = async () => {
    try {
      const response = await fetch('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('Cart could not be refreshed.');
      const cart = await response.json();
      qa('[data-v4-cart-count]').forEach((badge) => {
        badge.textContent = String(cart.item_count || 0);
        badge.hidden = !cart.item_count;
      });
      return cart;
    } catch (_) {
      return null;
    }
  };

  const ensureCartBackdrop = () => {
    let backdrop = q('.v4-cart-backdrop');
    if (backdrop) return backdrop;
    backdrop = document.createElement('button');
    backdrop.type = 'button';
    backdrop.className = 'v4-cart-backdrop';
    backdrop.setAttribute('aria-label', 'Close bag');
    backdrop.addEventListener('click', async () => {
      const drawer = q('#cart-drawer');
      if (drawer && typeof drawer.close === 'function') await drawer.close();
      document.body.classList.remove('v4-cart-open');
    });
    document.body.append(backdrop);
    return backdrop;
  };

  const openCart = async () => {
    ensureCartBackdrop();
    const drawer = q('#cart-drawer');
    if (!drawer) {
      window.location.assign('/cart');
      return;
    }
    try {
      await customElements.whenDefined('theme-drawer');
      drawer.setAttribute('no-persist', '');
      if (typeof drawer.open === 'function') drawer.open();
      document.body.classList.add('v4-cart-open');
      track('cart_open');
    } catch (_) {
      window.location.assign('/cart');
    }
  };

  const replaceCartSection = (html) => {
    if (!html) return false;
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const incoming = q('#shopify-section-cart-drawer-section', parsed);
    const current = q('#shopify-section-cart-drawer-section');
    if (!incoming || !current) return false;
    current.replaceWith(incoming);
    return true;
  };

  const addToCart = async (button) => {
    const id = button.dataset.variantId;
    if (!id) return;
    const card = button.closest('[data-v4-card]');
    const productTitle = card?.dataset.productTitle || 'Fragrance';
    const vendor = card?.dataset.productVendor || '';
    const variantTitle = button.dataset.variantTitle || q('[data-v4-selection]', card)?.textContent?.trim() || '';
    const original = button.textContent;

    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    button.textContent = 'Adding…';

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{ id: Number(id), quantity: 1 }],
          sections: ['cart-drawer-section'],
          sections_url: window.location.pathname
        })
      });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.description || payload.message || 'Unable to add this size.');
      replaceCartSection(payload.sections?.['cart-drawer-section']);
      const cart = await updateCartCount();
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart, payload } }));
      track('add_to_cart', { product: productTitle, vendor, variant: variantTitle, variantId: id });
      button.textContent = 'Added to Bag';
      showToast(`${productTitle} ${variantTitle} added to your bag.`.replace(/\s+/g, ' ').trim());
      await openCart();
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Unable to add this size.');
      button.textContent = original;
    } finally {
      window.setTimeout(() => {
        button.disabled = false;
        button.removeAttribute('aria-busy');
        button.textContent = original;
      }, 800);
    }
  };

  const selectVariant = (button) => {
    if (!(button instanceof HTMLButtonElement) || button.disabled) return;
    const card = button.closest('[data-v4-card]');
    if (!card) return;

    qa('[data-v4-size]', card).forEach((size) => size.setAttribute('aria-pressed', String(size === button)));

    const variantId = button.dataset.variantId || '';
    const variantTitle = button.dataset.variantTitle || button.textContent?.trim() || '';
    const priceValue = button.dataset.price || '';
    const compareValue = button.dataset.compare || '';

    const selection = q('[data-v4-selection]', card);
    if (selection) selection.textContent = variantTitle;

    const price = q('[data-v4-price]', card);
    if (price && priceValue) price.textContent = priceValue;

    const compare = q('[data-v4-compare]', card);
    if (compare) {
      compare.textContent = compareValue;
      compare.hidden = !compareValue;
    }

    const cardImage = q('.v4-card__media img', card);
    const imageSrc = button.dataset.imageSrc || '';
    if (cardImage && imageSrc) {
      cardImage.src = imageSrc;
      cardImage.srcset = '';
      cardImage.alt = button.dataset.imageAlt || `${card.dataset.productTitle || 'Fragrance'} ${variantTitle}`;
    }

    const variantUrl = button.dataset.variantUrl || '';
    if (variantUrl) qa('[data-v4-card-link]', card).forEach((link) => link.setAttribute('href', variantUrl));

    const add = q('[data-v4-add]', card);
    if (add) {
      add.dataset.variantId = variantId;
      add.dataset.variantTitle = variantTitle;
      add.disabled = !variantId;
      add.textContent = variantId ? `Add ${variantTitle} to Bag` : 'Unavailable';
      const productTitle = card.dataset.productTitle || 'fragrance';
      add.setAttribute('aria-label', `Add ${productTitle} ${variantTitle} to bag`);
    }

    track('variant_selected', {
      product: card.dataset.productTitle || '',
      vendor: card.dataset.productVendor || '',
      variant: variantTitle,
      variantId
    });
  };

  const syncRail = (rail) => {
    const trackElement = q('[data-v4-rail-track]', rail);
    const previous = q('[data-v4-rail-prev]', rail);
    const next = q('[data-v4-rail-next]', rail);
    if (!trackElement) return;
    const maxScroll = Math.max(trackElement.scrollWidth - trackElement.clientWidth, 0);
    if (previous) previous.disabled = trackElement.scrollLeft <= 2;
    if (next) next.disabled = trackElement.scrollLeft >= maxScroll - 2;
  };

  const moveRail = (button, direction) => {
    const rail = button.closest('[data-v4-rail]');
    const trackElement = q('[data-v4-rail-track]', rail);
    if (!trackElement) return;
    const card = q('[data-v4-card]', trackElement);
    const styles = getComputedStyle(trackElement);
    const gap = parseFloat(styles.columnGap || styles.gap || '0');
    const distance = Math.max((card?.getBoundingClientRect().width || trackElement.clientWidth * .75) + gap, 240);
    trackElement.scrollBy({ left: distance * direction, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
    track('product_rail_move', { direction: direction > 0 ? 'next' : 'previous' });
  };

  const initRails = () => {
    qa('[data-v4-rail]').forEach((rail) => {
      if (rail.dataset.v4Ready === 'true') return;
      rail.dataset.v4Ready = 'true';
      const trackElement = q('[data-v4-rail-track]', rail);
      if (!trackElement) return;
      trackElement.tabIndex = 0;
      trackElement.addEventListener('scroll', () => syncRail(rail), { passive: true });
      trackElement.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        const control = event.key === 'ArrowRight' ? q('[data-v4-rail-next]', rail) : q('[data-v4-rail-prev]', rail);
        if (control && !control.disabled) moveRail(control, event.key === 'ArrowRight' ? 1 : -1);
      });
      syncRail(rail);
    });
  };

  const initBenefits = () => {
    qa('[data-v4-benefits]').forEach((benefits) => {
      if (benefits.dataset.v4Ready === 'true') return;
      benefits.dataset.v4Ready = 'true';
      const mobile = window.matchMedia('(max-width: 620px)');
      const items = qa('.v4-benefit', benefits);
      const dots = qa('.v4-benefits__dots span', benefits);
      let activeIndex = 0;
      let autoplayTimer = 0;
      let restartTimer = 0;
      let scrollFrame = 0;

      const updateDots = () => dots.forEach((dot, index) => {
        if (index === activeIndex) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });

      const goTo = (index, behavior = 'smooth') => {
        if (!items.length) return;
        activeIndex = (index + items.length) % items.length;
        benefits.scrollTo({
          left: activeIndex * benefits.clientWidth,
          behavior: reduceMotion.matches ? 'auto' : behavior
        });
        updateDots();
      };

      const stopAutoplay = () => {
        window.clearInterval(autoplayTimer);
        autoplayTimer = 0;
      };

      const startAutoplay = () => {
        stopAutoplay();
        if (!mobile.matches || reduceMotion.matches || items.length < 2) return;
        autoplayTimer = window.setInterval(() => goTo(activeIndex + 1), 3800);
      };

      const pauseForInteraction = () => {
        stopAutoplay();
        window.clearTimeout(restartTimer);
        restartTimer = window.setTimeout(startAutoplay, 7000);
      };

      const sync = () => {
        const carousel = mobile.matches && items.length > 1;
        benefits.tabIndex = carousel ? 0 : -1;
        if (carousel && benefits.dataset.hintId) benefits.setAttribute('aria-describedby', benefits.dataset.hintId);
        else benefits.removeAttribute('aria-describedby');
        if (carousel) goTo(activeIndex, 'auto');
        else {
          stopAutoplay();
          benefits.scrollTo({ left: 0, behavior: 'auto' });
        }
        startAutoplay();
      };
      benefits.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        const direction = event.key === 'ArrowRight' ? 1 : -1;
        pauseForInteraction();
        goTo(activeIndex + direction);
      });
      benefits.addEventListener('scroll', () => {
        if (!mobile.matches || scrollFrame) return;
        scrollFrame = window.requestAnimationFrame(() => {
          scrollFrame = 0;
          activeIndex = Math.max(0, Math.min(items.length - 1, Math.round(benefits.scrollLeft / Math.max(benefits.clientWidth, 1))));
          updateDots();
        });
      }, { passive: true });
      benefits.addEventListener('pointerdown', pauseForInteraction, { passive: true });
      benefits.addEventListener('focusin', stopAutoplay);
      benefits.addEventListener('focusout', startAutoplay);
      benefits.addEventListener('mouseenter', stopAutoplay);
      benefits.addEventListener('mouseleave', startAutoplay);
      window.addEventListener('resize', sync, { passive: true });
      mobile.addEventListener?.('change', sync);
      reduceMotion.addEventListener?.('change', sync);
      updateDots();
      sync();
    });
  };

  const initSmartHeader = () => {
    const headerGroup = q('#header-group');
    const header = q('[data-v4-header]', headerGroup);
    if (!headerGroup || !header || headerGroup.dataset.v4ScrollReady === 'true') return;
    headerGroup.dataset.v4ScrollReady = 'true';
    headerGroup.dataset.v4Scroll = 'shown';
    const scrollCandidate = q('.page-wrapper');
    const scrollRoot = scrollCandidate && scrollCandidate.scrollHeight > scrollCandidate.clientHeight + 1 ? scrollCandidate : window;
    const readScrollY = () => Math.max(scrollRoot === window ? window.scrollY : scrollRoot.scrollTop, 0);
    let lastY = readScrollY();
    let frame = 0;

    const forceVisible = () => {
      const menu = q('[data-v4-menu]');
      const search = q('[data-v4-search]');
      return headerGroup.matches(':focus-within') || (menu && !menu.hidden) || (search && !search.hidden);
    };

    const show = () => { headerGroup.dataset.v4Scroll = 'shown'; };
    const update = () => {
      frame = 0;
      const currentY = readScrollY();
      const change = currentY - lastY;
      if (currentY <= 24 || forceVisible()) show();
      else if (change > 7 && currentY > 120) headerGroup.dataset.v4Scroll = 'hidden';
      else if (change < -7) show();
      if (Math.abs(change) > 7 || currentY <= 24) lastY = currentY;
    };

    const requestUpdate = () => {
      if (!frame) frame = window.requestAnimationFrame(update);
    };

    scrollRoot.addEventListener('scroll', requestUpdate, { passive: true });
    header.addEventListener('focusin', show);
    header.addEventListener('pointerenter', show);
    const menu = q('[data-v4-menu]');
    const search = q('[data-v4-search]');
    if (menu) new MutationObserver(show).observe(menu, { attributes: true, attributeFilter: ['hidden'] });
    if (search) new MutationObserver(show).observe(search, { attributes: true, attributeFilter: ['hidden'] });
  };

  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const tracked = target.closest('[data-v4-track]');
    if (tracked) track('cta_click', { placement: tracked.dataset.v4Track || '', href: tracked.getAttribute('href') || '' });

    const navTrigger = target.closest('[data-v4-nav-trigger]');
    if (navTrigger) {
      event.preventDefault();
      const item = navTrigger.closest('[data-v4-nav-item]');
      const panel = q('[data-v4-nav-panel]', item);
      const open = navTrigger.getAttribute('aria-expanded') !== 'true';
      closeNavs(open ? item : null);
      navTrigger.setAttribute('aria-expanded', String(open));
      if (panel) panel.hidden = !open;
      setSearch(false);
      if (open) track('navigation_open', { label: navTrigger.textContent?.trim() || '' });
      return;
    }

    if (target.closest('[data-v4-menu-open]')) { event.preventDefault(); setMenu(true); return; }
    if (target.closest('[data-v4-menu-close]')) { event.preventDefault(); setMenu(false); return; }

    if (target.closest('[data-v4-search-toggle]')) {
      event.preventDefault();
      const trigger = target.closest('[data-v4-search-toggle]');
      const open = trigger.getAttribute('aria-expanded') !== 'true';
      closeNavs();
      setSearch(open);
      return;
    }

    const size = target.closest('[data-v4-size]');
    if (size) { event.preventDefault(); selectVariant(size); return; }

    const add = target.closest('[data-v4-add]');
    if (add) { event.preventDefault(); addToCart(add); return; }

    if (target.closest('[data-v4-cart-toggle]')) { event.preventDefault(); openCart(); return; }
    if (target.closest('[data-v4-rail-prev]')) { event.preventDefault(); moveRail(target.closest('[data-v4-rail-prev]'), -1); return; }
    if (target.closest('[data-v4-rail-next]')) { event.preventDefault(); moveRail(target.closest('[data-v4-rail-next]'), 1); return; }

    if (!target.closest('[data-v4-nav-item]')) closeNavs();
    if (!target.closest('[data-v4-search]') && !target.closest('[data-v4-search-toggle]')) setSearch(false);
  });

  document.addEventListener('keydown', (event) => {
    const menu = q('[data-v4-menu]');
    if (event.key === 'Tab' && menu && !menu.hidden) {
      const panel = q('.v4-menu__panel', menu);
      const focusable = qa('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])', panel)
        .filter((element) => !element.hidden && element.getAttribute('aria-hidden') !== 'true' && element.getClientRects().length > 0 && !element.closest('details:not([open])'));
      if (focusable.length) {
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
          return;
        }
        if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
          return;
        }
      }
    }
    if (event.key !== 'Escape') return;
    closeNavs();
    setSearch(false);
    setMenu(false);
  });

  document.addEventListener('theme-drawer:close', (event) => {
    if (event.target instanceof HTMLElement && event.target.id === 'cart-drawer') {
      document.body.classList.remove('v4-cart-open');
      qa('[data-v4-cart-toggle]').forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));
    }
  });
  document.addEventListener('theme-drawer:open', (event) => {
    if (event.target instanceof HTMLElement && event.target.id === 'cart-drawer') {
      document.body.classList.add('v4-cart-open');
      qa('[data-v4-cart-toggle]').forEach((trigger) => trigger.setAttribute('aria-expanded', 'true'));
    }
  });
  document.addEventListener('shopify:section:load', () => {
    initRails();
    initBenefits();
    initSmartHeader();
  });

  window.addEventListener('resize', () => qa('[data-v4-rail]').forEach(syncRail), { passive: true });

  ensureCartBackdrop();
  initRails();
  initBenefits();
  initSmartHeader();
  updateCartCount();
})();
